import React, { useEffect, useState } from 'react';
import { FileSpreadsheet, FileText, Loader2, Calendar, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { exportAPI } from '../services/api';
import AdminExportAuto from './AdminExportAuto';
import AdminAccountantAccess from './AdminAccountantAccess';

const SHEETS = [
  { key: 'orders', label: 'Commandes', desc: 'Une ligne par commande : HT, TVA, TTC, coût d\'achat, marge' },
  { key: 'invoices', label: 'Factures & avoirs', desc: 'Journal des ventes : une ligne par facture et par avoir (n° de pièce, HT / TVA / TTC, dont montage, règlement, pièce d\'origine)' },
  { key: 'order_lines', label: 'Lignes de commande', desc: 'Détail article par article avec conditionnement, remise quantité et marge' },
  { key: 'purchase_orders', label: 'Bons de commande', desc: 'Achats fournisseurs ligne par ligne, statut et réception' },
  { key: 'products', label: 'Produits & marges', desc: 'Catalogue : prix HT/TTC, prix d\'achat, marge, stocks' },
];

const monthRange = (offset = 0) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - offset);
  const from = d.toISOString().slice(0, 10);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from, to: end.toISOString().slice(0, 10) };
};

// Admin: accounting export (xlsx multi-sheet + csv per sheet) with period filter
const AdminExportPanel = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    setSummary(null);
    exportAPI.summary(from, to).then(setSummary).catch(() => setSummary({}));
  }, [from, to]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); toast.success('Export téléchargé'); } catch (e) { toast.error(e.response?.data?.detail || 'Export impossible'); } finally { setBusy(''); }
  };
  const preset = (r) => { setFrom(r.from); setTo(r.to); };

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Export comptable</h1>
        <p className="text-sm text-gray-500">Téléchargez vos ventes, achats fournisseurs et marges pour votre expert-comptable. Montants HT / TVA / TTC, numéros de commande et de facture.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-5 mb-6 flex flex-wrap items-end gap-4" data-testid="export-period">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Calendar size={12} /> Du</label>
          <Input data-testid="export-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Au</label>
          <Input data-testid="export-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-44" />
        </div>
        <div className="flex flex-wrap gap-2 pb-0.5">
          <Button type="button" variant="outline" size="sm" data-testid="export-preset-month" onClick={() => preset(monthRange(0))}>Ce mois</Button>
          <Button type="button" variant="outline" size="sm" data-testid="export-preset-last-month" onClick={() => preset(monthRange(1))}>Mois dernier</Button>
          <Button type="button" variant="outline" size="sm" data-testid="export-preset-year" onClick={() => { const y = new Date().getFullYear(); setFrom(`${y}-01-01`); setTo(`${y}-12-31`); }}>Cette année</Button>
          <Button type="button" variant="ghost" size="sm" data-testid="export-preset-all" onClick={() => { setFrom(''); setTo(''); }}>Tout</Button>
        </div>
        <p className="text-xs text-gray-500 ml-auto">{from || to ? `Période : ${from || '…'} → ${to || 'aujourd\'hui'}` : 'Toutes les données (sans filtre de période)'}. Les produits & marges ne dépendent pas de la période.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-5 mb-6 flex flex-wrap items-center justify-between gap-4 border-l-4 border-green-600" data-testid="export-xlsx-block">
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={28} className="text-green-600" />
          <div>
            <p className="font-semibold text-gray-900">Classeur Excel complet (.xlsx)</p>
            <p className="text-sm text-gray-500">{SHEETS.length} feuilles : {SHEETS.map(s => s.label).join(', ')}.</p>
          </div>
        </div>
        <Button data-testid="export-xlsx" onClick={() => run('xlsx', () => exportAPI.xlsx(from, to))} disabled={busy === 'xlsx'} className="bg-green-600 hover:bg-green-700">
          {busy === 'xlsx' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />} Télécharger l'Excel
        </Button>
      </div>

      <AdminExportAuto />
      <AdminAccountantAccess />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="export-csv-list">
        {SHEETS.map(s => (
          <div key={s.key} className="bg-white rounded-lg shadow-sm p-4 flex items-center justify-between gap-3" data-testid={`export-sheet-${s.key}`}>
            <div className="flex items-start gap-3 min-w-0">
              <FileText size={20} className="text-[#1e3a5f] flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-medium text-gray-900">{s.label} <span className="text-xs text-gray-400 font-normal" data-testid={`export-count-${s.key}`}>{summary ? `· ${summary[SHEETS.find(x => x.key === s.key).label] ?? 0} ligne(s)` : ''}</span></p>
                <p className="text-xs text-gray-500">{s.desc}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" data-testid={`export-csv-${s.key}`} onClick={() => run(s.key, () => exportAPI.csv(s.key, from, to))} disabled={busy === s.key}>
              {busy === s.key ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} CSV
            </Button>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-gray-500">CSV séparé par « ; », encodage UTF-8 (compatible Excel FR), décimales avec virgule.</p>
    </>
  );
};

export default AdminExportPanel;
